import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { reportSpamRequest, runMailMoves, moveErrorMessage } from '../utils/reportSpam';
import { moveMailRequest } from '../utils/moveMail';
import MoveToFolderButton from './MoveToFolderButton';
import ConfirmationModal from './ConfirmationModal';
import AlertModal from './AlertModal';
import MailListRow from './MailListRow';
import MailReadingPane from './MailReadingPane';
import './Inbox.css';
import './FolderView.css';

const isImapMail = (m) => m && (m.source === 'imap' || m.uid);
const getMailUid = (m) => m?.uid ?? m?.imapUid;
const getMailKey = (m) => String(getMailUid(m) ?? m?._id ?? m?.messageId ?? '');

// Every folder that should feed into the unified "All Mail" list.
const ALL_MAIL_SOURCES = [
  { url: '/api/mail/inbox', label: 'Inbox' },
  { url: '/api/mail/sent', label: 'Sent' },
  { url: '/api/mail/drafts', label: 'Drafts' },
  { url: '/api/mail/starred', label: 'Starred' },
  { url: '/api/mail/server/snoozed', label: 'Snooze' },
];

/**
 * AllMail Component
 * Aggregates mails from every folder (Inbox, Sent, Drafts, Snooze, Starred)
 * into one unified list for the logged-in user.
 */
const AllMail = ({ currentUser }) => {
  const [mails, setMails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMail, setSelectedMail] = useState(null);
  const [selectedMailIds, setSelectedMailIds] = useState([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  useEffect(() => {
    if (currentUser) {
      fetchAllMail();
      
      // Auto-refresh all mails every 30 seconds (without showing loading spinner)
      const interval = setInterval(() => {
        fetchAllMail(false);
      }, 30000); // 30 seconds
      
      return () => clearInterval(interval);
    }
  }, [currentUser]);

  const fetchAllMail = async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    setError('');
    try {
      const results = await Promise.allSettled(
        ALL_MAIL_SOURCES.map((source) => axiosInstance.get(source.url))
      );

      const merged = [];
      results.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value?.data?.success) {
          const list = result.value.data.data || [];
          list.forEach((mail) => merged.push({ ...mail, _folderLabel: ALL_MAIL_SOURCES[index].label }));
        }
      });

      // De-duplicate mails that live in more than one folder (e.g. a starred
      // inbox mail). Prefer the first occurrence following the source order.
      const seen = new Set();
      const deduped = [];
      merged.forEach((mail) => {
        const dedupeKey = String(mail.messageId || getMailKey(mail));
        if (!dedupeKey || seen.has(dedupeKey)) return;
        seen.add(dedupeKey);
        deduped.push(mail);
      });

      deduped.sort(
        (a, b) => new Date(b.createdAt || b.date || 0) - new Date(a.createdAt || a.date || 0)
      );

      setMails(deduped);
    } catch (error) {
      console.error('Error fetching all mails:', error);
      setError(error.response?.data?.message || 'Error fetching mails. Please try again.');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleMailClick = async (mail) => {
    setSelectedMail(mail);

    if (isImapMail(mail)) {
      const uid = getMailUid(mail);
      if (!uid) return;
      try {
        const detail = await axiosInstance.get(`/api/mail/server/message/${uid}`);
        if (detail.data.success) {
          setSelectedMail({ ...detail.data.data, _folderLabel: mail._folderLabel });
        }
      } catch (error) {
        console.error('Error loading message:', error);
      }
      return;
    }

    // Local (Mongo) mail — mark read unless it's a draft.
    if (!mail.isRead && mail.folder !== 'draft') {
      try {
        await axiosInstance.patch(`/api/mail/mark-read/${mail._id}`);
        setMails((prev) =>
          prev.map((m) => (getMailKey(m) === getMailKey(mail) ? { ...m, isRead: true } : m))
        );
        setSelectedMail({ ...mail, isRead: true });
      } catch (error) {
        console.error('Error marking mail as read:', error);
      }
    }
  };

  const handleToggleStar = async (e, mail) => {
    e.stopPropagation();
    const mailKey = getMailKey(mail);
    try {
      let response;
      if (isImapMail(mail)) {
        const uid = getMailUid(mail);
        if (!uid) return;
        response = await axiosInstance.patch(`/api/mail/server/star/${uid}`, { starred: !mail.isStarred });
      } else {
        response = await axiosInstance.patch(`/api/mail/star/${mail._id}`);
      }

      if (response.data.success) {
        const nextStarred = response.data.data?.isStarred ?? !mail.isStarred;
        setMails((prev) =>
          prev.map((m) => (getMailKey(m) === mailKey ? { ...m, isStarred: nextStarred } : m))
        );
        if (selectedMail && getMailKey(selectedMail) === mailKey) {
          setSelectedMail({ ...selectedMail, isStarred: nextStarred });
        }
      }
    } catch (error) {
      console.error('Error toggling star:', error);
    }
  };

  const handleCloseMail = () => {
    setSelectedMail(null);
  };

  // Selection handlers
  const handleSelectMail = (mailId) => {
    setSelectedMailIds(prev => 
      prev.includes(mailId) 
        ? prev.filter(id => id !== mailId)
        : [...prev, mailId]
    );
  };

  const handleSelectAll = () => {
    if (selectedMailIds.length === mails.length) {
      setSelectedMailIds([]);
    } else {
      setSelectedMailIds(mails.map(getMailKey));
    }
  };

  const handleDeselectAll = () => {
    setSelectedMailIds([]);
  };

  // Bulk actions
  const handleBulkDelete = async () => {
    if (selectedMailIds.length === 0) {
      setAlertMessage('Please select at least one mail to delete');
      setShowAlert(true);
      return;
    }
    setShowBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    if (selectedMailIds.length === 0) return;
    
    setShowBulkDeleteConfirm(false); // Close modal immediately

    try {
      const selectedMails = mails.filter((m) => selectedMailIds.includes(getMailKey(m)));
      const deletePromises = selectedMails.map((mail) => {
        if (isImapMail(mail)) {
          const uid = getMailUid(mail);
          return axiosInstance.patch(`/api/mail/server/trash/${uid}`).catch(() => null);
        }
        if (mail.folder === 'draft') {
          return axiosInstance.delete(`/api/mail/draft/${mail._id}`).catch(() => null);
        }
        return axiosInstance.patch(`/api/mail/move-to-trash/${mail._id}`).catch(() => null);
      });
      await Promise.all(deletePromises);

      setMails(mails.filter((m) => !selectedMailIds.includes(getMailKey(m))));
      if (selectedMail && selectedMailIds.includes(getMailKey(selectedMail))) {
        setSelectedMail(null);
      }
      setSelectedMailIds([]);
      fetchAllMail(false);
    } catch (error) {
      console.error('Error deleting mails:', error);
      setAlertMessage('Error deleting mails. Please try again.');
      setShowAlert(true);
    }
  };

  const handleBulkMarkUnread = async () => {
    if (selectedMailIds.length === 0) return;

    try {
      setMails(mails.map((m) =>
        selectedMailIds.includes(getMailKey(m)) ? { ...m, isRead: false } : m
      ));
      setSelectedMailIds([]);
    } catch (error) {
      console.error('Error marking mails as unread:', error);
    }
  };

  const handleBulkMarkSpam = async () => {
    const targets = mails.filter((mail) => selectedMailIds.includes(getMailKey(mail)));
    if (targets.length === 0) return;

    try {
      const { moved, failed } = await runMailMoves(targets, reportSpamRequest);
      if (moved.length) {
        const movedKeys = new Set(moved.map(getMailKey));
        setMails((prev) => prev.filter((mail) => !movedKeys.has(getMailKey(mail))));
        setSelectedMailIds((prev) => prev.filter((id) => !movedKeys.has(id)));
        if (selectedMail && movedKeys.has(getMailKey(selectedMail))) setSelectedMail(null);
        window.dispatchEvent(new CustomEvent('inboxUpdated'));
      }
      if (failed.length) {
        setAlertMessage(moveErrorMessage(failed, 'Could not report spam. Please try again.'));
        setShowAlert(true);
      }
    } catch (error) {
      console.error('Error reporting spam:', error);
      setAlertMessage('Could not report spam. Please try again.');
      setShowAlert(true);
    }
  };

  const handleMoveToFolder = async (destination) => {
    const targets = mails.filter((mail) => selectedMailIds.includes(getMailKey(mail)));
    if (!targets.length) return;

    try {
      const { moved, failed } = await runMailMoves(
        targets,
        (mail) => moveMailRequest(mail, destination, mail.folder || 'inbox')
      );
      if (moved.length) {
        const movedKeys = new Set(moved.map(getMailKey));
        setMails((prev) => prev.filter((mail) => !movedKeys.has(getMailKey(mail))));
        setSelectedMailIds((prev) => prev.filter((id) => !movedKeys.has(id)));
        if (selectedMail && movedKeys.has(getMailKey(selectedMail))) setSelectedMail(null);
        window.dispatchEvent(new CustomEvent('inboxUpdated'));
      }
      if (failed.length) {
        setAlertMessage(moveErrorMessage(failed, 'Could not move mail. Please try again.'));
        setShowAlert(true);
      }
    } catch (error) {
      console.error('Error moving mail:', error);
      setAlertMessage('Could not move mail. Please try again.');
      setShowAlert(true);
    }
  };

  const handleBulkDownload = () => {
    if (selectedMailIds.length === 0) return;
    const selectedMails = mails.filter(m => selectedMailIds.includes(getMailKey(m)));
    selectedMails.forEach(mail => {
      const mailContent = `From: ${mail.from}\nTo: ${mail.to}\nSubject: ${mail.subject}\nDate: ${formatFullDate(mail.createdAt)}\n\n${mail.body}`;
      const blob = new Blob([mailContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${mail.subject || 'mail'}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
  };

  const formatFullDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="inbox-container">
        <div className="loading">Loading all mails...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="inbox-container">
        <div className="error-message">{error}</div>
        <button onClick={fetchAllMail} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={`inbox-container premium-mail-dashboard ${selectedMail ? 'mail-detail-open' : ''}`}>
      <div className="inbox-header">
        <div>
          <p className="inbox-eyebrow">ATOZASMAIL Workspace</p>
          <h2>All Mail</h2>
        </div>
        <div className="inbox-header-actions">
          <p className="mail-count">
            {mails.length} {mails.length === 1 ? 'mail' : 'mails'}
          </p>
        </div>
      </div>

      <div className={`inbox-content reading-pane-no-split gmail-split-layout${selectedMail ? ' has-detail-open full-detail-view' : ''}`}>
        <div className={`mail-list-container ${selectedMail ? 'hidden' : 'full-width'}`}>
          {mails.length > 0 && (
            <div className="mail-toolbar">
              <div className="toolbar-left">
                <button 
                  className="toolbar-btn checkbox-btn"
                  onClick={selectedMailIds.length > 0 ? handleDeselectAll : handleSelectAll}
                  title={selectedMailIds.length > 0 ? "Deselect all" : "Select all"}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    {selectedMailIds.length > 0 && (
                      <line x1="7" y1="12" x2="17" y2="12" strokeLinecap="round"/>
                    )}
                  </svg>
                  {selectedMailIds.length > 0 && (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" className="dropdown-icon">
                      <path d="M7 10l5 5 5-5z"/>
                    </svg>
                  )}
                </button>
                
                <button 
                  className="toolbar-btn"
                  onClick={fetchAllMail}
                  title="Refresh"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 4v6h-6"/>
                    <path d="M1 20v-6h6"/>
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                  </svg>
                </button>
                
                {selectedMailIds.length > 0 && (
                  <>
                    <button 
                      className="toolbar-btn"
                      onClick={handleBulkDownload}
                      title="Download"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7 10 12 15 17 10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                    </button>
                    
                    <button 
                      className="toolbar-btn"
                      onClick={handleBulkMarkSpam}
                      title="Report spam"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2"/>
                        <line x1="12" y1="8" x2="12" y2="12"/>
                        <line x1="12" y1="16" x2="12.01" y2="16"/>
                      </svg>
                    </button>
                    
                    <button 
                      className="toolbar-btn"
                      onClick={handleBulkDelete}
                      title="Delete"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                      </svg>
                    </button>
                  </>
                )}
              </div>
              
              {selectedMailIds.length > 0 && (
                <>
                  <div className="toolbar-separator"></div>
                  <div className="toolbar-right">
                    <button 
                      className="toolbar-btn"
                      onClick={handleBulkMarkUnread}
                      title="Mark as unread"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                      <circle cx="18" cy="4" r="3" fill="currentColor" className="notification-dot"/>
                    </button>
                    
                    <MoveToFolderButton onMove={handleMoveToFolder} />
                    
                    <button 
                      className="toolbar-btn"
                      title="More options"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="12" cy="5" r="2"/>
                        <circle cx="12" cy="12" r="2"/>
                        <circle cx="12" cy="19" r="2"/>
                      </svg>
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
          <div className="mail-list">
            {mails.length === 0 ? (
              <div className="empty-inbox">
                <p>No mails</p>
                <p className="empty-hint">All your mails will appear here</p>
              </div>
            ) : (
              mails.map((mail) => {
                const mailKey = getMailKey(mail);
                return (
                  <MailListRow
                    key={mailKey}
                    mail={mail}
                    mailKey={mailKey}
                    currentUserEmail={currentUser?.email}
                    badge={mail._folderLabel}
                    isChecked={selectedMailIds.includes(mailKey)}
                    isOpen={selectedMail && getMailKey(selectedMail) === mailKey}
                    isUnread={!mail.isRead && !mail.seen && mail.folder !== 'draft'}
                    isStarred={mail.isStarred}
                    onOpen={handleMailClick}
                    onToggleCheck={handleSelectMail}
                    onToggleStar={handleToggleStar}
                  />
                );
              })
            )}
          </div>
        </div>

        {selectedMail && (
          <MailReadingPane
            mail={selectedMail}
            folderLabel={selectedMail._folderLabel || 'All Mail'}
            onClose={handleCloseMail}
          />
        )}
      </div>

      <ConfirmationModal
        isOpen={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        onConfirm={confirmBulkDelete}
        message={`Are you sure you want to delete ${selectedMailIds.length} mail(s)?`}
        confirmText="OK"
        cancelText="Cancel"
      />

      <AlertModal
        isOpen={showAlert}
        onClose={() => setShowAlert(false)}
        message={alertMessage}
      />
    </div>
  );
};

export default AllMail;


